import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { FaChevronDown, FaChevronRight, FaLifeRing } from 'react-icons/fa';
import SupportCaseService from '../../services/supportCaseService';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import { SUPPORT_CASE_STATUS_LABELS, formatSupportCaseDate } from './supportCaseUtils';
import './Ayuda.css';

const Ayuda = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { timezone } = useOrganizationTimezone();
  const [supportCases, setSupportCases] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(true);

  useEffect(() => {
    const token = auth.user?.access_token;
    if (!token) {
      setIsLoadingCases(false);
      return;
    }

    let cancelled = false;
    const loadCases = async () => {
      try {
        const result = await SupportCaseService.getCases(token);
        if (!cancelled) setSupportCases(result.data || []);
      } catch (error) {
        console.error('Error loading support cases:', error);
      } finally {
        if (!cancelled) setIsLoadingCases(false);
      }
    };

    loadCases();
    return () => { cancelled = true; };
  }, [auth.user?.access_token]);

  const [expandedSections, setExpandedSections] = useState({
    'gestion-de-equipo': false,
    'politicas-del-sistema': false,
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  return (
    <div className="ayuda-page-container">
      <div className="ayuda-header">
        <div className="ayuda-header-top">
          <div>
            <h1>Ayuda</h1>
            <p className="ayuda-subtitle">Encuentra respuestas a las preguntas más frecuentes</p>
          </div>
          <button className="ayuda-request-btn" onClick={() => navigate('/ayuda/solicitar')}>
            <FaLifeRing />
            <span>Solicitar ayuda</span>
          </button>
        </div>
      </div>

      <div className="ayuda-content">
        <section className="ayuda-seccion">
          <h2>Solicitudes de ayuda</h2>
          {isLoadingCases ? (
            <div className="ayuda-loading">
              <div className="ayuda-spinner"></div>
            </div>
          ) : supportCases.length === 0 ? (
            <p className="ayuda-empty">No hay casos de soporte.</p>
          ) : (
            <div className="ayuda-case-list">
              {supportCases.map((supportCase) => (
                <button
                  key={supportCase.id}
                  type="button"
                  className="ayuda-case-item"
                  onClick={() => navigate(`/ayuda/casos/${supportCase.id}`)}
                >
                  <div className="ayuda-case-item-main">
                    <span className="ayuda-case-item-title">{supportCase.title}</span>
                    <span className="ayuda-case-item-meta">
                      {supportCase.createdByName && `${supportCase.createdByName} · `}
                      Última actividad: {formatSupportCaseDate(supportCase.lastActivityAt, timezone)}
                      {supportCase.lastCommentAuthorType === 'support' && ' · Respondido por soporte'}
                    </span>
                  </div>
                  <span className={`ayuda-status-badge ayuda-status-${supportCase.status}`}>
                    {SUPPORT_CASE_STATUS_LABELS[supportCase.status] || supportCase.status}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="ayuda-seccion">
          <div className="ayuda-seccion-header" onClick={() => toggleSection('gestion-de-equipo')}>
            <h2>Gestión de Equipo</h2>
            <span className="ayuda-seccion-icon">
              {expandedSections['gestion-de-equipo'] ? <FaChevronDown /> : <FaChevronRight />}
            </span>
          </div>

          {expandedSections['gestion-de-equipo'] && (
            <div className="ayuda-seccion-content">
              <div className="ayuda-card">
                <h3>¿Cómo agregar un nuevo miembro al equipo?</h3>
                <div className="ayuda-answer">
                  <p>Para agregar un nuevo miembro a tu clínica, sigue estos pasos:</p>
                  <ol>
                    <li>Ve a <strong>Configuración</strong> en el menú lateral</li>
                    <li>Selecciona <strong>Usuarios</strong></li>
                    <li>Haz clic en el botón <strong>"Agregar Usuario"</strong></li>
                    <li>Ingresa el correo electrónico del nuevo miembro</li>
                    <li>Ingresa el nombre y apellido del usuario</li>
                    <li>Selecciona el <strong>rol</strong> apropiado para el usuario</li>
                    <li>Haz clic en <strong>"Agregar Usuario"</strong></li>
                  </ol>
                  <p className="ayuda-note">El nuevo miembro recibirá un correo de invitación para unirse a tu clínica.</p>
                </div>
              </div>

              <div className="ayuda-card">
                <h3>¿Qué roles están disponibles y cómo asignarlos?</h3>
                <div className="ayuda-answer">
                  <p>Existen dos tipos de roles en el sistema:</p>

                  <h4>Roles Predefinidos</h4>
                  <ul>
                    <li><strong>Propietario</strong> - Tiene acceso completo a toda la clínica.</li>
                    <li><strong>Administrador</strong> - Tiene acceso completo a toda la clínica. La única operación que no puede realizar es remover algún miembro con rol de propietario.</li>
                  </ul>

                  <h4>Roles Personalizados</h4>
                  <p>Puedes crear roles a medida con permisos específicos:</p>
                  <ol>
                    <li>Ve a <strong>Configuración</strong> → <strong>Roles</strong></li>
                    <li>Haz clic en <strong>"Agregar Rol"</strong></li>
                    <li>Define un nombre descriptivo para el rol</li>
                    <li>Selecciona los permisos que tendrá el rol:
                      <ul>
                        <li>Ver reportes financieros</li>
                        <li>Ver reportes de citas</li>
                        <li>Ver datos médicos de pacientes</li>
                        <li>Ver datos de contacto de pacientes</li>
                        <li>Ver todas las agendas</li>
                        <li>Crear citas para otros usuarios</li>
                        <li>Ver todos los planes de tratamiento</li>
                      </ul>
                    </li>
                    <li>Guarda el rol</li>
                  </ol>
                  <p className="ayuda-note">Una vez creado el rol personalizado, podrás asignarlo a cualquier miembro del equipo al momento de agregarlo o al editar su información.</p>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="ayuda-seccion">
          <div className="ayuda-seccion-header" onClick={() => toggleSection('politicas-del-sistema')}>
            <h2>Políticas del Sistema</h2>
            <span className="ayuda-seccion-icon">
              {expandedSections['politicas-del-sistema'] ? <FaChevronDown /> : <FaChevronRight />}
            </span>
          </div>

          {expandedSections['politicas-del-sistema'] && (
            <div className="ayuda-seccion-content">
              <div className="ayuda-card">
                <h3>¿Por qué no puedo eliminar ciertos registros?</h3>
                <div className="ayuda-answer">
                  <p>Por razones de <strong>auditoría y cumplimiento legal</strong>, el sistema no permite eliminar ciertos registros como:</p>
                  <ul>
                    <li>Pagos y transacciones</li>
                    <li>Pacientes</li>
                    <li>Prescripciones médicas</li>
                  </ul>

                  <h4>Razones de esta política:</h4>
                  <ol>
                    <li><strong>Cumplimiento legal:</strong> Se requiere conservar un historial completo de todas las operaciones durante períodos establecidos por la ley.</li>
                    <li><strong>Trazabilidad:</strong> Mantener un registro histórico permite rastrear cualquier transacción para auditorías, investigaciones internas o resolución de disputas.</li>
                    <li><strong>Integridad de datos:</strong> Eliminar registros puede comprometer la integridad de otros datos relacionados, como estados de cuenta y reportes.</li>
                  </ol>

                  <p className="ayuda-note">Si necesitas invalidar un registro, el sistema ofrece opciones para marcarlo como cancelado o anulado, preservando así el historial completo para auditoría.</p>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Ayuda;
